/**
 * ADR 0418 — one web bundle, many organisations: which hostname is this, and is it safe to trust.
 * plan: 4-Archives/tms/deeptrack-plan/0418-one-web-brand-by-hostname.md (D1 · D6 · D8 · D9 · D13)
 *
 * The API cannot load this package at runtime (ADR 0150) and keeps a copy in
 * `apps/api/src/common/lib/host.ts`; `host.spec.ts` there runs `host.cases.json` against BOTH
 * implementations, so a change made on one side only fails a test instead of shipping.
 *
 * Every place these rules apply (keep this list current):
 * - web  `lib/brand-state.ts`            — which host to ask about, cache key, error codes, the
 *                                          only place that decides to redirect (D6)
 * - api  `common/cors-origin.ts`         — which browser origins may call the API (D9)
 * - api  public brand controller (D4)    — host lookup, response keys, error codes      [Phase 2]
 * - api  `TenantGuard` host lock (D8)    — Origin → host → organisation                 [Phase 2]
 * - api  admin org update (D2/D11)       — validates `domains` before saving            [Phase 2]
 * - api  env validation (D13)            — `DEV_HOST_ALIASES`, refused in production    [Phase 2]
 *
 * Security notes — every function here treats its input as attacker-controlled:
 * - inputs are length-capped BEFORE any regex runs, and every regex is linear (no nested
 *   quantifiers), so a hostile header cannot make us spin;
 * - only plain ASCII hostnames are accepted. Browsers already hand us punycode in
 *   `location.hostname`/`Origin`, so refusing raw Unicode costs nothing and closes the
 *   look-alike-letters door;
 * - anything that is not *just* a hostname (userinfo, path, query, wildcard, IP-literal
 *   brackets) is refused rather than "cleaned up" — cleaning is how `evil.com#.good.com` wins.
 */

/** Longest hostname DNS allows (RFC 1035). Checked before any regex. */
export const HOST_MAX_LENGTH = 253;
const LABEL_MAX_LENGTH = 63;
/** Raw inputs (a header, a query param) longer than this are refused outright. */
const RAW_INPUT_MAX_LENGTH = 300;

const LABEL_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const PORT_RE = /^[0-9]{1,5}$/;

/**
 * A bare hostname, or `null` when the input is not one.
 *
 * Lower-cases, drops one `:port` and one trailing dot. Refuses everything else that is not a
 * hostname — it never tries to salvage a hostname out of a URL.
 */
export function normalizeHost(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length === 0 || raw.length > RAW_INPUT_MAX_LENGTH) return null;
  let h = raw.trim().toLowerCase();
  if (h.length === 0) return null;

  // One optional `:port` at the end. Two colons = IPv6 or garbage: both refused.
  const colon = h.indexOf(':');
  if (colon !== -1) {
    if (h.indexOf(':', colon + 1) !== -1) return null;
    const port = h.slice(colon + 1);
    if (!PORT_RE.test(port) || Number(port) > 65535) return null;
    h = h.slice(0, colon);
  }

  if (h.endsWith('.')) h = h.slice(0, -1);
  if (h.length === 0 || h.length > HOST_MAX_LENGTH) return null;

  const labels = h.split('.');
  for (const label of labels) {
    if (label.length === 0 || label.length > LABEL_MAX_LENGTH) return null;
    if (!LABEL_RE.test(label)) return null;
  }
  return h;
}

/**
 * D2 — a hostname an org may own: normalized, with a dot, not `localhost`/`*.localhost` (dev
 * aliases live in env, D13), not a bare IPv4. One rule for the API (admin update, script) and
 * the super admin form. Returns the normalized host or null.
 */
export function servableDomain(raw: unknown): string | null {
  const host = normalizeHost(raw);
  if (!host || !host.includes('.')) return null;
  if (host === 'localhost' || host.endsWith('.localhost')) return null;
  if (/^[0-9.]+$/.test(host)) return null;
  return host;
}

/**
 * Hosts where an unknown-host answer must NOT redirect to the marketing site — the plain
 * dev URLs people use every day. **Exact names only**: `abc.localhost` is deliberately not
 * exempt, so the redirect can be exercised on a dev machine (D13).
 */
export const NO_REDIRECT_HOSTS = ['localhost', '127.0.0.1'] as const;

export function isNoRedirectHost(host: string): boolean {
  return (NO_REDIRECT_HOSTS as readonly string[]).includes(host);
}

export interface ParsedOrigin {
  scheme: 'http' | 'https';
  host: string;
  /** `null` when absent or equal to the scheme's default port. */
  port: number | null;
}

const ORIGIN_RE = /^(https?):\/\/([^/?#@\\\s]+)$/;

/**
 * Split a browser `Origin` header into its parts, or `null` when it is not a plain
 * `http(s)://host[:port]` origin.
 *
 * Not `new URL()`: the URL parser *repairs* input (backslashes, stray paths, userinfo), and a
 * security check must refuse what it does not understand rather than guess. `"null"` (sandboxed
 * iframes, `file://`) is refused here — callers decide what that means for them.
 */
export function parseOrigin(raw: unknown): ParsedOrigin | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > RAW_INPUT_MAX_LENGTH) return null;
  const m = ORIGIN_RE.exec(raw.trim().toLowerCase());
  if (!m) return null;
  const scheme = m[1] as 'http' | 'https';
  const authority = m[2];
  const host = normalizeHost(authority);
  if (!host) return null;
  // normalizeHost already validated the port; read it back for the caller.
  const colon = authority.lastIndexOf(':');
  let port: number | null = colon === -1 ? null : Number(authority.slice(colon + 1));
  if ((scheme === 'https' && port === 443) || (scheme === 'http' && port === 80)) port = null;
  return { scheme, host, port };
}

/** Error codes of the public brand endpoint (D4). Web and API compare against THESE, never a literal. */
export const BRAND_ERROR = {
  HOST_NOT_FOUND: 'HOST_NOT_FOUND',
  HOST_INVALID: 'HOST_INVALID',
} as const;
export type BrandErrorCode = (typeof BRAND_ERROR)[keyof typeof BRAND_ERROR];

/**
 * D8 — the 403 `error` TenantGuard returns when a logged-in user calls with an org the page's
 * domain is not locked to. The web shows the wrong-domain notice (mockup frame 3) on this code.
 */
export const HOST_ORG_MISMATCH = 'HOST_ORG_MISMATCH';

/** What `GET /public/brand?host=` answers on 200 — and nothing else (D4: no tenant_id, ever). */
export interface PublicBrand {
  org_slug: string;
  org_name: string;
  login_title: string;
  browser_title: string;
  logo_url: string | null;
  favicon_url: string | null;
  /**
   * ADR 0421 D2 — login-page behaviour of this org. `null` = not set → the web bundle's own default
   * (env at build). Show the OTP switch? Show "Powered by · version"?
   */
  login_otp: boolean | null;
  show_powered_by: boolean | null;
}

/** The exact key set of `PublicBrand`, for tests that must notice a field being added. */
export const PUBLIC_BRAND_KEYS = [
  'org_slug',
  'org_name',
  'login_title',
  'browser_title',
  'logo_url',
  'favicon_url',
  'login_otp',
  'show_powered_by',
] as const satisfies readonly (keyof PublicBrand)[];

/** D17 — the neutral name (login page + tab) when no org owns the host. API env `PLATFORM_NAME` overrides it. */
export const DEFAULT_PLATFORM_NAME = 'DeepTrack';

/** Display text from the API is capped before it reaches the page. */
export const BRAND_TEXT_MAX_LENGTH = 120;
/** URLs (logo, favicon, redirect target) longer than this are refused. */
export const BRAND_URL_MAX_LENGTH = 2048;

/**
 * `localStorage` key for the last good brand of a host (D6). Bump `v1` when an OLD cached value would be
 * misread by the new parser. Adding a field the parser treats as optional (ADR 0421's `login_otp` /
 * `show_powered_by` → missing = null = bundle default) does not need a bump — and a bump would throw
 * away every cached brand, which is exactly what keeps the login page branded while the API is down.
 */
export function brandCacheKey(host: string): string {
  return `brand:v1:${host}`;
}

/** A target of `DEV_HOST_ALIASES`: look the brand up by a real domain, or straight by org slug. */
export type DevHostTarget = { kind: 'domain'; domain: string } | { kind: 'slug'; slug: string };

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/;
const DEV_ALIAS_MAX_ENTRIES = 50;
const DEV_ALIAS_RAW_MAX_LENGTH = 4000;

/**
 * Parse `DEV_HOST_ALIASES` — `alias=target,alias=target` — for dev machines (D13).
 *
 * Throws on anything malformed: a typo here must stop the API from booting with a clear
 * message, never be skipped silently (a silently-skipped alias looks exactly like "the
 * feature is broken").
 *
 * Every alias must end in `.localhost`. That is the safety catch: even if this variable ever
 * leaked into a real deployment, it could only re-point names that no real visitor can send,
 * never shadow `cca.deepinnoline.com`. Production refuses the variable outright anyway (D13).
 */
export function parseDevHostAliases(raw: string | undefined | null): Map<string, DevHostTarget> {
  const out = new Map<string, DevHostTarget>();
  if (raw === undefined || raw === null || raw.trim() === '') return out;
  if (raw.length > DEV_ALIAS_RAW_MAX_LENGTH) throw new Error('DEV_HOST_ALIASES is too long');

  const entries = raw.split(',').map((e) => e.trim()).filter((e) => e !== '');
  if (entries.length > DEV_ALIAS_MAX_ENTRIES) throw new Error('DEV_HOST_ALIASES has too many entries');

  for (const entry of entries) {
    const eq = entry.indexOf('=');
    if (eq === -1) throw new Error(`DEV_HOST_ALIASES: "${entry}" has no "="`);
    const alias = normalizeHost(entry.slice(0, eq));
    const targetRaw = entry.slice(eq + 1).trim();
    if (!alias) throw new Error(`DEV_HOST_ALIASES: "${entry}" — alias is not a hostname`);
    if (!alias.endsWith('.localhost')) {
      throw new Error(`DEV_HOST_ALIASES: "${alias}" — aliases must end in .localhost`);
    }
    if (out.has(alias)) throw new Error(`DEV_HOST_ALIASES: "${alias}" appears twice`);

    if (targetRaw.startsWith('slug:')) {
      const slug = targetRaw.slice('slug:'.length);
      if (!SLUG_RE.test(slug)) throw new Error(`DEV_HOST_ALIASES: "${entry}" — not a valid org slug`);
      out.set(alias, { kind: 'slug', slug });
    } else {
      const domain = normalizeHost(targetRaw);
      if (!domain) throw new Error(`DEV_HOST_ALIASES: "${entry}" — target is not a hostname`);
      if (domain === 'localhost' || domain.endsWith('.localhost')) {
        throw new Error(`DEV_HOST_ALIASES: "${entry}" — target must be a real domain, not another alias`);
      }
      out.set(alias, { kind: 'domain', domain });
    }
  }
  return out;
}
